import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AccessRole, ButtonColumnType, CustomTableComponent, CustomTitle, DataColumnType, FormGroupErrorMessageComponent, ICustomTable,
  IHeader, LoadingPanelService, LocalService, noWhitespaceValidator, Page, Utils, DATE_WITH_HOUR_FORMAT
} from 'lib-base';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { IGetSecUsuario, ISecCargo, ITipoIdentificacion, RegistrarSecUsuarioRequest, UsuarioActualizacionInfo, VwSecDependencia, VwSecUsuario } from '../../../../shared/interfaces/database/seguridad';
import { SecUsuarioService } from '../../../../services/database/secUsuario.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SecCargoService } from '../../../../services/database/secCargo.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { TipoIdentificacionService } from '../../../../services/database/tipoIdentificacion.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { SecDependenciaService } from '../../../../services/database/secDependencia.service';
import { SecUsuarioSolicitudService } from '../../../../services/database/sec-usuario-solicitud.service';
import { ConfirmPopupModule } from 'primeng/confirmpopup';

@Component({
  selector: 'app-usuarios',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, CustomTableComponent,
    TableModule, ToastModule, ButtonModule, DialogModule, InputTextModule, DropdownModule,
    FormGroupErrorMessageComponent, CustomTitle, InputNumberModule, MultiSelectModule, ConfirmPopupModule
  ],
  providers: [ConfirmationService],
  templateUrl: './usuarios.component.html',
  styleUrls: ['./usuarios.component.scss']
})
export class UsuariosComponent implements OnInit {
  usuarios: IGetSecUsuario[] = [];
  usuario !: VwSecUsuario;
  usuarioForm !: FormGroup;

  trans: any = {};
  customTable !: ICustomTable;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;

  formTitle = '';
  displayModal = false;
  private _edit !: IGetSecUsuario | undefined;
  tipoIdentificaciones: ITipoIdentificacion[] = [];
  cargos: ISecCargo[] = [];
  dependencias: VwSecDependencia[] = [];
  role = AccessRole;

  constructor(private readonly _fb: FormBuilder, private readonly _ms: MessageService, private readonly _t: TranslateService, private readonly _ti: TipoIdentificacionService,
    private readonly _sc: SecCargoService, private readonly _sd: SecDependenciaService, public readonly ls: LocalService, private readonly _suss: SecUsuarioSolicitudService, private readonly _usuarioService: SecUsuarioService,
    private readonly _lps: LoadingPanelService, private readonly _cs: ConfirmationService) {
    this.configureForm();
  }

  ngOnInit(): void {
    this.translate();
    this.initializePage();
  }

  get isNewForm(): boolean {
    return this._edit === undefined;
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      title: this._t.get('components.app-admin-usuarios.app-usuarios.title'),
      createBtnMsg: this._t.get('components.app-admin-usuarios.app-usuarios.createBtnMsg'),
      requiereRolSeguridad: this._t.get('msg.requiere-rol-seguridad'),
      dependencia: this._t.get('common.dependencia'),
      correo: this._t.get('common.correo'),
      nombre: this._t.get('common.nombre'),
      apellido: this._t.get('common.apellido'),
      identificacion: this._t.get('common.identificacion'),
      cargoCodificacion: this._t.get('common.cargoCodificacion'),
      fechaModificacion: this._t.get('common.fechaModificacion'),
      fechaRegistro: this._t.get('common.fechaRegistro'),
      fechaInactivacion: this._t.get('common.fechaInactivacion'),
      success: this._t.get('common.success'),
      nuevoUsuario: this._t.get('msg.nuevoUsuario'),
      editarUsuario: this._t.get('msg.editarUsuario'),
      solicitudCreada: this._t.get('msg.solicitudCreada'),
      usuarioActualizado: this._t.get('msg.usuarioActualizado'),
      usuarioEliminado: this._t.get('msg.usuarioEliminado'),
      advertencia: this._t.get('common.advertencia'),
      usuarioCreadoMensaje: this._t.get('msg.usuarioCreadoMensaje'),
      estasSeguroEliminarUsuario: this._t.get('msg.estasSeguroEliminarUsuario'),
      activo: this._t.get('common.activo'),
      usuarioActivado: this._t.get('msg.usuarioActivado'),
      completeCampos: this._t.get('form.msg.complete-campos'),
    }));

    this.configureColumns();
  }

  private configureColumns() {
    this.customTable = {
      header: {
      } as IHeader,
      columns: [
        {
          field: 'dependencia',
          header: this.trans.dependencia,
          sort: true,
          columnFilter: true,
          class: 'brand-table-tag mx-auto my-auto font-13',
          dataType: DataColumnType.TEXT,
        },
        {
          field: 'correo',
          header: this.trans.correo,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'nombre',
          header: this.trans.nombre,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'apellido',
          header: this.trans.apellido,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'identificacion',
          header: this.trans.identificacion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'cargoCodificacion',
          header: this.trans.cargoCodificacion,
          sort: false,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          dataType: DataColumnType.TEXT,
        },
        {
          field: 'fechaModificacion',
          header: this.trans.fechaModificacion,
          sort: false,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          format: DATE_WITH_HOUR_FORMAT,
          dataType: DataColumnType.DATE,
        },
        {
          field: 'fechaRegistro',
          header: this.trans.fechaRegistro,
          sort: false,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          format: DATE_WITH_HOUR_FORMAT,
          dataType: DataColumnType.DATE,
        },
        {
          field: 'fechaInactivacion',
          header: this.trans.fechaInactivacion,
          sort: false,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: false,
          format: DATE_WITH_HOUR_FORMAT,
          dataType: DataColumnType.DATE,
        },
        {
          field: 'activo',
          header: this.trans.activo,
          sort: true,
          class: 'button-edit p-button-outlined',
          width: '6rem',
          buttonType: ButtonColumnType.CHECK,
          onClick: (event: Event, data: IGetSecUsuario) => this.desactivarUsuario(event, data),
        },
        {
          field: 'edit',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          tooltip: {
            message: this.trans.requiereRolSeguridad
          },
          onClick: (event: Event, data: IGetSecUsuario) => this.openEditModal(data),
        },
        {
          field: 'delete',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-0',
          buttonType: ButtonColumnType.DELETE,
          onClick: (event: Event, data: IGetSecUsuario) => this.desactivarUsuario(event, data),
          headerBtn: {
            icon: 'pi pi-plus',
            class: 'pt-2 pb-2 pl-0 pr-0',
            onClick: (event: Event) => this.openNewModal(),
            disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          },
          headerTooltip:{
            message: this.trans.requiereRolSeguridad,
            disabled: this.ls.hasRole(this.role.ADMIN_SEC)
          }
        }
      ],
      settings: {
        table: {
          rows: 10,
        }
      }
    } as ICustomTable;
  }

  initializePage() {
    this.getUsuarios();
    this.getCargos();
    this.getTipoIdentificaciones();
    this.getDependencias();
  }

  configureForm() {
    this.usuarioForm = this._fb.group({
      nombre: [null, [Validators.required, Validators.maxLength(255), noWhitespaceValidator]],
      apellido: [null, [Validators.required, Validators.maxLength(255), noWhitespaceValidator]],
      tipoIdentificacionId: [null, Validators.required],
      identificacion: [null, [Validators.required, Validators.maxLength(20)]],
      correo: [null, [Validators.required, Validators.email, Validators.maxLength(100), noWhitespaceValidator]],
      cargoId: [null, [Validators.required]],
      dependenciaIds: [null, Validators.required]
    });
  }

  openNewModal() {
    this.formTitle = this.trans.nuevoUsuario;
    this._edit = undefined;
    this.resetForm();
    this.displayModal = true;
  }

  openEditModal(data: IGetSecUsuario) {
    this.formTitle = this.trans.editarUsuario;
    this._edit = data;
    this.usuarioForm.get('nombre')?.setValue(data.nombre);
    this.usuarioForm.get('apellido')?.setValue(data.apellido);
    this.usuarioForm.get('tipoIdentificacionId')?.setValue(data.tipoIdentificacionId);
    this.usuarioForm.get('identificacion')?.setValue(data.identificacion);
    this.usuarioForm.get('correo')?.setValue(data.correo);
    this.usuarioForm.get('cargoId')?.setValue(data.cargoId);
    this.usuarioForm.get('dependenciaIds')?.setValue(data.dependenciaIds?.filter(id => id !== null));
    this.displayModal = true;
  }

  resetForm() {
    this.usuarioForm.reset();
  }

  onSubmit() {
    if (this.usuarioForm.invalid) {
      Utils.markAllFormControlsAsTouched(this.usuarioForm);
      this._ms.add({ severity: 'warn', summary: 'Advertencia', detail: this.trans.completeCampos });
      return;
    }

    let request: RegistrarSecUsuarioRequest = {
      nombre: this.usuarioForm.get('nombre')?.value,
      apellido: this.usuarioForm.get('apellido')?.value,
      cargoId: this.usuarioForm.get('cargoId')?.value,
      correo: this.usuarioForm.get('correo')?.value,
      tipoIdentificacionId: this.usuarioForm.get('tipoIdentificacionId')?.value,
      identificacion: this.usuarioForm.get('identificacion')?.value,
      dependenciaIds: this.usuarioForm.get('dependenciaIds')?.value
    };

    if (this.isNewForm) {
      this.createSecUsuarioSolicitud(request);
    } else {
      this.updateUsuario(request);
    }
  }

  //#region API Calls
  private createSecUsuarioSolicitud(request: RegistrarSecUsuarioRequest) {
    this._lps.show(true);
    this._suss.createSecUsuarioSolicitud(request).subscribe({
      next: (response: boolean) => {
        this._ms.add({
          severity: 'success',
          summary: this.trans.success,
          detail: this.trans.usuarioCreadoMensaje,
          life: 10000
        });
        this.displayModal = false;
        this.getUsuarios();
        this._lps.hide();
      }
    });
  }

  private updateUsuario(request: RegistrarSecUsuarioRequest) {
    if (!this._edit) return;
    this._lps.show(true);
    this._usuarioService.updateUsuario(this._edit.id, request).subscribe({
      next: (info: UsuarioActualizacionInfo[]) => {
        if (info.length > 0) {
          let messages: string[] = ['Actualizacion parcial.'];
          info.forEach(e => {
            if (e.tipo === 1) {
              messages.push("La dependencia " + e.nombre + " no se puede remover porque tiene actividades pendientes.");
            } else if (e.tipo === 2) {
              messages.push("El cargo " + e.nombre + " no se puede remover porque tiene actividades pendientes.");
            } else if (e.tipo === 3) {
              messages.push("El cargo " + e.nombre + " no se puede remover porque tiene actividades de pqrsd pendientes.");
            }
          });
          this._ms.add({ severity: 'warn', summary: this.trans.advertencia, detail: messages.join("\n"), life: 10000 });
        } else {
          this._ms.add({ severity: 'success', summary: this.trans.success, detail: "Usuario actualizado exitosamente" });
        }
        this.displayModal = false;
        this.getUsuarios();
        this._lps.hide();
      }
    });
  }

  desactivarUsuario(event: Event, usuario: IGetSecUsuario) {
    if (usuario.activo) {
      this._cs.confirm({
        target: event.target as EventTarget,
        message: this.trans.estasSeguroEliminarUsuario,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this._lps.show(true);
          this._usuarioService.desactivarUsuario(usuario.id).subscribe({
            next: (response: boolean) => {
              this._lps.hide();
              this._ms.add({ severity: 'success', summary: this.trans.success, detail: this.trans.usuarioEliminado });
              this.getUsuarios();
            }
          });
        }
      });
    } else {
      this._lps.show(true);
      this._usuarioService.desactivarUsuario(usuario.id).subscribe({
        next: (response: boolean) => {
          this._lps.hide();
          this._ms.add({ severity: 'success', summary: this.trans.success, detail: this.trans.usuarioActivado });
          this.getUsuarios();
        }
      });
    }

  }

  getTipoIdentificaciones() {
    this._ti.getTipoIdentificaciones().subscribe({
      next: (response: ITipoIdentificacion[]) => {
        this.tipoIdentificaciones = response;
      }
    });
  }

  private getUsuarios() {
    this._usuarioService.getUsuarios().subscribe({
      next: (data: IGetSecUsuario[]) => {
        this.usuarios = data;
        this.customTableComponent.data = data
      }
    });
  }

  private getCargos() {
    this._sc.getCargos().subscribe({
      next: (response: ISecCargo[]) => {
        this.cargos = response;
      }
    });
  }

  private getDependencias() {
    this._sd.getDependencias().subscribe({
      next: (page: Page<VwSecDependencia>) => {
        this.dependencias = page.content;
      }
    });
  }
  //#endregion API Calls
}
